import {
  ReviewPostDto,
  FeedbackPostDto,
  PasswordlessRequestResultDto,
  PasswordlessVerifyDto,
  UserEditDto,
} from "@citychallenge/api-public";
import { computed } from "mobx";
import { useContext } from "react";

import { ServiceInterface } from "../service.interface";
import { LoggerService } from "../logger";
import { AuthService } from "../auth";
import { ApiService } from "../api";
import { MessageService } from "../message";
import { Environment } from "../../environment";
import { StorageService } from "../storage";
import { Subscription } from "rxjs";
import { TransTokens } from "../../i18n/trans-tokens";
import { createContext } from "react";
import { LoginAdapterInterface } from "./login-adapter.interface";
import { LoginError } from "./login-error";
import { UserModel } from "./user.model";

const tokens = TransTokens.create({
  userAccountSaved: "Your user account was successfully updated!",
});

export const UserServiceContext = createContext<UserService>(null as any);

export const useActiveUser = () => {
  return useContext(UserServiceContext).user;
};

export const useNickname = () => {
  return useContext(UserServiceContext).visibleNickname;
};

export class UserService implements ServiceInterface {
  public context = UserServiceContext;

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly logger: LoggerService,
    public readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly messageService: MessageService,
    private readonly storageService: StorageService,
    private readonly loginAdapter: LoginAdapterInterface
  ) {}

  public nickname = this.storageService.getPreference("_USER_NICKNAME");

  @computed
  public get hasNickName() {
    return !!(this.nickname.value || this.user?.nickname);
  }

  @computed
  public get visibleNickname() {
    return this.nickname.value || this.user?.nickname || this.user?.name;
  }

  public updateUser = async (
    userInfo: UserEditDto,
    showMessage = true,
    divisionId?: string
  ) => {
    if (!this.user) return;
    try {
      const { data } = await this.apiService.clientPrivate.editAccount(
        userInfo,
        divisionId
          ? {
              headers: {
                "X-App-Division-Id": divisionId,
              },
            }
          : undefined
      );
      // Updated nickname
      if (userInfo.nickname) this.nickname.setValue(data.nickname);
      this.authService.updateUserInfo(data);

      if (showMessage)
        this.messageService.show({
          message: tokens.userAccountSaved,
        });
    } catch (e) {
      if (showMessage) this.messageService.showError(e);
      else throw e;
    }
  };

  public serviceWillMount() {
    this.loginAdapter.configureGoogleLogin();
  }

  public serviceWillUnmount() {
    for (const subscription of this.subscriptions) subscription.unsubscribe();
  }

  @computed
  public get loggedIn() {
    return !!this.authService.currentAccessToken;
  }

  @computed
  public get loggedInWithFullAccount() {
    return !!this.user?.email;
  }

  @computed
  public get hasBuilderRole() {
    const user = this.user;
    if (!user) return false;

    return user.role !== "client";
  }

  @computed
  public get user() {
    if (
      !this.authService.currentAccessToken ||
      !this.authService.currentAccessToken.user
    )
      return undefined;
    return new UserModel(
      this.authService.currentAccessToken.user,
      this.nickname
    );
  }

  @computed
  public get token() {
    return this.authService.currentAccessToken;
  }

  public async authStatus(divisionId?: string) {
    if (!this.user) return undefined;

    try {
      if (divisionId) {
        return await this.apiService.clientPrivate.divisionAuthStatus(
          divisionId
        );
      } else {
        return await this.apiService.clientPrivate.authStatus();
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        throw new LoginError.UnauthenticatedError();
      }
      throw e;
    }
  }

  public facebookLogin = async () => {
    if (this.loggedInWithFullAccount)
      throw new LoginError.AlreadyLoggedInError();

    const facebookToken = await this.loginAdapter.facebookLogin(this.logger);

    try {
      const accessToken = await this.apiService.clientPublic.facebookLogin(
        facebookToken
      );
      this.authService.setAccessToken(accessToken.data);
      return accessToken.data;
    } catch (e) {
      this.logger.capture(e);
      throw new LoginError.UnknownError();
    }
  };

  public googleLogin = async () => {
    if (this.loggedInWithFullAccount)
      throw new LoginError.AlreadyLoggedInError();

    const googleToken = await this.loginAdapter.googleLogin(this.logger);

    try {
      const accessToken = await this.apiService.clientPublic.googleLogin(
        googleToken
      );
      this.authService.setAccessToken(accessToken.data);
      return accessToken.data;
    } catch (e) {
      this.logger.capture(e);
      throw new LoginError.UnknownError();
    }
  };

  public appleLogin = async () => {
    if (this.loggedInWithFullAccount)
      throw new LoginError.AlreadyLoggedInError();

    const requestResult = await this.loginAdapter.appleLogin(this.logger);

    const { authorizationCode, fullName } = requestResult;
    if (!authorizationCode) {
      throw new LoginError.FailedError();
    }

    try {
      const accessToken = await this.apiService.clientPublic.appleLogin({
        authorizationCode,
        fullName,
        bundleId: Environment.identifier,
      });
      this.authService.setAccessToken(accessToken.data);
      return accessToken.data;
    } catch (e) {
      this.logger.capture(e);
      throw new LoginError.UnknownError();
    }
  };

  public throwInvalidDomain(e: any) {
    if (e.response?.status === 401) {
      const isEp = Environment.identifier === "nl.energyplatform";

      if (e.response.data?.message === "ERR_INVALID_DOMAIN") {
        throw isEp
          ? new LoginError.InvalidDomainErrorEP()
          : new LoginError.InvalidDomainError();
      }
      if (e.response.data?.message === "ERR_INVALID_EMAIL") {
        throw isEp
          ? new LoginError.InvalidEmailErrorEP()
          : new LoginError.InvalidEmailError();
      }
    }
  }

  public async passwordlessLogin(
    email: string,
    divisionId?: string,
    url?: string
  ): Promise<PasswordlessRequestResultDto> {
    if (this.loggedInWithFullAccount)
      throw new LoginError.AlreadyLoggedInError();

    try {
      const { data } = await this.apiService.clientPublic.passwordlessLogin({
        email,
        divisionId,
        url,
      });

      return data;
    } catch (e) {
      if (e.response && e.response.status === 400) {
        throw new LoginError.InvalidMailAddressError();
      }
      if (e.response && e.response.status === 0) {
        throw new LoginError.NetworkError();
      }

      this.throwInvalidDomain(e);

      throw new LoginError.UnknownError();
    }
  }

  public passwordDeeplinkHandler = (verify: PasswordlessVerifyDto) => {
    this.messageService.catch(this.passwordlessVerify(verify)).then(() => {
      this.messageService.show({ message: "You are now logged in!" });
    });
  };

  public async passwordlessVerify(verify: PasswordlessVerifyDto) {
    if (this.loggedInWithFullAccount)
      throw new LoginError.AlreadyLoggedInError();

    try {
      const { data } = await this.apiService.clientPublic.passwordlessVerify(
        verify
      );

      return data;
    } catch (e) {
      if (e.response?.status === 400) {
        if (e.response.data?.message === "TOKEN_EXPIRED") {
          throw new LoginError.ExpiredTokenError(e);
        }
        throw new LoginError.InvalidTokenError(e);
      }
      if (e.response?.status === 0) {
        throw new LoginError.NetworkError(e);
      }
      throw new LoginError.UnknownError(e);
    }
  }

  public anonymousLogin = async () => {
    try {
      const deviceToken = await this.authService.getDeviceToken();
      const secret = await this.authService.getDeviceSecret();

      const { data } = await this.apiService.clientPublic.anonymousLogin(
        {
          deviceToken,
          secret,
        },
        {
          timeout: 5000,
        }
      );

      this.authService.setAccessToken(data);
    } catch (e) {
      if (e.response?.status === 0) {
        throw new LoginError.AnonNetworkError();
      }

      throw new LoginError.AnonUnknownError();
    }
  };

  public logout = async () => {
    if (!this.authService.isLoggedIn) return null;

    this.nickname.clearValue();
    this.apiService.clientPrivate.clientLogout();
    this.authService.setAccessToken(undefined);
    return null;
  };

  public removeOwnAccount = async () => {
    if (!this.authService.isLoggedIn) return null;

    await this.apiService.clientPrivate.removeOwnAccount();
    this.authService.setAccessToken(undefined);

    return null;
  };

  public postReview(_gameId: string, _entity: ReviewPostDto) {
    return Promise.resolve();
  }

  public postFeedback(sceneId: string, entity: FeedbackPostDto) {
    return this.apiService.clientPublic.postFeeback(sceneId, entity);
  }
}
